import React, { useState, useCallback, useEffect } from 'react';
import { Upload, X, FileText, Image, Video, Award, File, CheckSquare, Square, GripVertical, Loader2, BookOpen, Presentation, BarChart, FileSpreadsheet, FileImage, Film, Trophy, GraduationCap, Code, Newspaper, Wand2, Lightbulb, ListChecks, Copy, Check, Download, AlertCircle, Users, Bookmark, Heart, Star } from 'lucide-react';
import { analyzeDocument } from '../services/fileAnalysis';
import { extractTextFromFile, truncateForAI } from '../utils/documentTextExtractor';
import { generateBulletsFromEvaluation, extractSkillsFromDocument, summarizeDocument, suggestFileCategory } from '../services/aiService';
import { uploadFileToS3 } from '../services/api';

const PortfolioUpload = ({ onFilesAdded, existingPortfolio = [], onSkillsExtracted, onBulletsGenerated, hideExistingFiles = false }) => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState(existingPortfolio);
  const [analyzingFiles, setAnalyzingFiles] = useState(new Set());
  const [draggedFile, setDraggedFile] = useState(null);
  const [dragOverCategory, setDragOverCategory] = useState(null);

  // AI action states
  const [aiActionLoading, setAiActionLoading] = useState({}); // { [fileId]: 'summarize' | 'skills' | 'bullets' }
  const [aiErrors, setAiErrors] = useState({}); // { [fileId]: 'error message' }
  const [extractedSkills, setExtractedSkills] = useState({}); // { [fileId]: ['skill1', 'skill2'] }
  const [generatedBullets, setGeneratedBullets] = useState({}); // { [fileId]: ['bullet1', 'bullet2'] }
  const [copiedBullet, setCopiedBullet] = useState(null); // fileId-index string

  // Cache extracted text to avoid re-extraction
  const [textCache, setTextCache] = useState({}); // { [fileId]: 'extracted text' }

  // S3 upload states
  const [uploadingFiles, setUploadingFiles] = useState(new Set()); // fileIds currently uploading to S3
  const [uploadErrors, setUploadErrors] = useState({}); // { [fileId]: 'error message' }

  const categories = [
    { id: 'word', label: 'Word Documents', icon: <FileText className="w-4 h-4" />, color: 'blue' },
    { id: 'excel', label: 'Excel Spreadsheets', icon: <FileSpreadsheet className="w-4 h-4" />, color: 'green' },
    { id: 'powerpoint', label: 'PowerPoint Presentations', icon: <Presentation className="w-4 h-4" />, color: 'orange' },
    { id: 'images', label: 'Pictures/Images', icon: <FileImage className="w-4 h-4" />, color: 'purple' },
    { id: 'videos', label: 'Videos', icon: <Film className="w-4 h-4" />, color: 'red' },
    { id: 'awards', label: 'Awards', icon: <Trophy className="w-4 h-4" />, color: 'yellow' },
    { id: 'certificates', label: 'Certificates', icon: <GraduationCap className="w-4 h-4" />, color: 'indigo' },
    { id: 'projects', label: 'Projects', icon: <Code className="w-4 h-4" />, color: 'pink' },
    { id: 'leadership', label: 'Leadership & Team Development', icon: <Users className="w-4 h-4" />, color: 'emerald' },
    { id: 'case_studies', label: 'Case Studies', icon: <Bookmark className="w-4 h-4" />, color: 'violet' },
    { id: 'community_service', label: 'Community & Service', icon: <Heart className="w-4 h-4" />, color: 'rose' },
    { id: 'performance_evaluations', label: 'Performance Evaluations', icon: <Star className="w-4 h-4" />, color: 'sky' },
    { id: 'publications', label: 'Publications', icon: <Newspaper className="w-4 h-4" />, color: 'teal' },
    { id: 'training', label: 'Training Materials', icon: <BookOpen className="w-4 h-4" />, color: 'cyan' },
    { id: 'reports', label: 'Reports & Analysis', icon: <BarChart className="w-4 h-4" />, color: 'amber' },
    { id: 'other', label: 'Other', icon: <File className="w-4 h-4" />, color: 'gray' }
  ];

  // File types that support text extraction (and thus AI features)
  const AI_SUPPORTED_EXTENSIONS = ['doc', 'docx', 'pdf', 'pptx', 'ppt', 'xlsx', 'xls', 'txt', 'md', 'csv'];

  const isAiSupported = (fileObj) => {
    const ext = (fileObj.name || '').split('.').pop().toLowerCase();
    return AI_SUPPORTED_EXTENSIONS.includes(ext);
  };

  useEffect(() => {
    if (existingPortfolio && existingPortfolio.length > 0) {
      setUploadedFiles(existingPortfolio);
    }
  }, [existingPortfolio]);

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  }, [selectedCategories]);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const categorizeFile = (file) => {
    const extension = file.name.split('.').pop().toLowerCase();
    const type = file.type;
    const fileName = file.name.toLowerCase();

    // Keyword-based categorization (checked first, most specific)
    if (fileName.includes('leadership') || fileName.includes('team dev') || fileName.includes('mentoring') || fileName.includes('coaching') || fileName.includes('supervision')) return 'leadership';
    if (fileName.includes('case study') || fileName.includes('case-study') || fileName.includes('casestudy') || fileName.includes('scenario')) return 'case_studies';
    if (fileName.includes('community') || fileName.includes('volunteer') || fileName.includes('service') || fileName.includes('outreach') || fileName.includes('civic')) return 'community_service';
    if (fileName.includes('evaluation') || fileName.includes('performance review') || fileName.includes('appraisal') || fileName.includes('assessment') || fileName.includes('rating')) return 'performance_evaluations';
    if (fileName.includes('award') || fileName.includes('trophy')) return 'awards';
    if (fileName.includes('certificate') || fileName.includes('cert')) return 'certificates';
    if (fileName.includes('project')) return 'projects';
    if (fileName.includes('publication') || fileName.includes('article')) return 'publications';
    if (fileName.includes('training') || fileName.includes('workshop')) return 'training';
    if (fileName.includes('report') || fileName.includes('analysis')) return 'reports';

    // Extension / MIME type fallback
    if (['doc', 'docx'].includes(extension)) return 'word';
    if (['xls', 'xlsx', 'csv'].includes(extension)) return 'excel';
    if (['ppt', 'pptx'].includes(extension)) return 'powerpoint';
    if (type.startsWith('image/')) return 'images';
    if (type.startsWith('video/')) return 'videos';
    return 'other';
  };

  const handleFiles = async (files) => {
    const filesToProcess = files.map(file => {
      let category;
      if (selectedCategories.length > 0) {
        const fileIndex = uploadedFiles.length;
        category = selectedCategories[fileIndex % selectedCategories.length];
      } else {
        category = categorizeFile(file);
      }

      return {
        file, // Kept temporarily for AI analysis; stripped before serialization
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        category,
        summary: '',
        uploadedAt: new Date().toISOString(),
        url: null
      };
    });

    const newFiles = [...uploadedFiles, ...filesToProcess];
    setUploadedFiles(newFiles);
    if (onFilesAdded) {
      // Strip File blobs before notifying parent (so serialization works)
      onFilesAdded(newFiles.map(({ file, ...rest }) => rest));
    }

    // Upload each file to S3 in parallel, then run AI analysis
    for (const fileObj of filesToProcess) {
      // ── S3 Upload ──
      setUploadingFiles(prev => new Set(prev).add(fileObj.id));
      try {
        const publicUrl = await uploadFileToS3(fileObj.file, 'portfolio');
        // Update the file entry with the S3 URL
        setUploadedFiles(prev => {
          const updated = prev.map(f =>
            f.id === fileObj.id ? { ...f, url: publicUrl } : f
          );
          if (onFilesAdded) onFilesAdded(updated.map(({ file, ...rest }) => rest));
          return updated;
        });
        console.log(`[S3 Upload] ${fileObj.name} uploaded to ${publicUrl}`);
      } catch (s3Err) {
        console.error(`[S3 Upload] Failed for ${fileObj.name}:`, s3Err);
        setUploadErrors(prev => ({ ...prev, [fileObj.id]: s3Err.message }));
      } finally {
        setUploadingFiles(prev => {
          const next = new Set(prev);
          next.delete(fileObj.id);
          return next;
        });
      }

      // ── AI Analysis (for supported file types) ──
      if (isAiSupported(fileObj)) {
        setAnalyzingFiles(prev => new Set(prev).add(fileObj.id));
        try {
          const analysis = await analyzeDocument(fileObj.file);
          let extractedText = '';

          if (analysis.extractedText) {
            extractedText = analysis.extractedText;
            setTextCache(prev => ({ ...prev, [fileObj.id]: analysis.extractedText }));
          }

          setUploadedFiles(prev => {
            const updated = prev.map(f =>
              f.id === fileObj.id ? { ...f, summary: analysis.summary } : f
            );
            if (onFilesAdded) onFilesAdded(updated.map(({ file, ...rest }) => rest));
            return updated;
          });

          // ── AI Auto-Categorization: ask AI to suggest a better category ──
          if (extractedText && extractedText.length >= 50) {
            try {
              const aiCategory = await suggestFileCategory(extractedText, fileObj.name);
              if (aiCategory && aiCategory !== 'other' && aiCategory !== fileObj.category) {
                console.log(`[AI Category] Reclassified "${fileObj.name}" from "${fileObj.category}" to "${aiCategory}"`);
                setUploadedFiles(prev => {
                  const updated = prev.map(f =>
                    f.id === fileObj.id ? { ...f, category: aiCategory } : f
                  );
                  if (onFilesAdded) onFilesAdded(updated.map(({ file, ...rest }) => rest));
                  return updated;
                });
              }
            } catch (catErr) {
              console.warn('[AI Category] Auto-categorization failed (non-critical):', catErr.message);
            }
          }
        } catch (error) {
          console.error('Error analyzing file:', error);
          setUploadedFiles(prev => {
            const updated = prev.map(f =>
              f.id === fileObj.id
                ? { ...f, summary: 'Unable to analyze this document. Please provide a manual summary.' }
                : f
            );
            if (onFilesAdded) onFilesAdded(updated.map(({ file, ...rest }) => rest));
            return updated;
          });
        } finally {
          setAnalyzingFiles(prev => {
            const next = new Set(prev);
            next.delete(fileObj.id);
            return next;
          });
        }
      }
    }
  };

  // ─────────────── AI Action Handlers ───────────────

  const getFileText = async (fileObj) => {
    if (textCache[fileObj.id]) return textCache[fileObj.id];

    // Use in-memory File blob if available (just uploaded this session)
    if (fileObj.file) {
      const text = await extractTextFromFile(fileObj.file);
      if (text) {
        setTextCache(prev => ({ ...prev, [fileObj.id]: text }));
      }
      return text;
    }

    // For saved files with a URL, fetch the file and extract text
    if (fileObj.url) {
      try {
        const response = await fetch(fileObj.url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        const fileName = fileObj.name || 'document';
        const file = new File([blob], fileName, { type: blob.type });
        const text = await extractTextFromFile(file);
        if (text) {
          setTextCache(prev => ({ ...prev, [fileObj.id]: text }));
        }
        return text;
      } catch (err) {
        console.error('[getFileText] Failed to fetch file from URL:', err);
        return '';
      }
    }

    return '';
  };

  const handleAiResummarize = async (fileObj) => {
    setAiActionLoading(prev => ({ ...prev, [fileObj.id]: 'summarize' }));
    setAiErrors(prev => ({ ...prev, [fileObj.id]: null }));
    try {
      const text = await getFileText(fileObj);
      if (!text || text.length < 20) {
        throw new Error('Could not extract enough text from this file.');
      }
      const truncated = truncateForAI(text, 3000);
      const summary = await summarizeDocument(truncated, fileObj.name);

      setUploadedFiles(prev => {
        const updated = prev.map(f =>
          f.id === fileObj.id ? { ...f, summary } : f
        );
        if (onFilesAdded) onFilesAdded(updated.map(({ file, ...rest }) => rest));
        return updated;
      });
    } catch (err) {
      console.error('[AI Summarize]', err);
      setAiErrors(prev => ({ ...prev, [fileObj.id]: err.message }));
    } finally {
      setAiActionLoading(prev => ({ ...prev, [fileObj.id]: null }));
    }
  };

  const handleAiExtractSkills = async (fileObj) => {
    setAiActionLoading(prev => ({ ...prev, [fileObj.id]: 'skills' }));
    setAiErrors(prev => ({ ...prev, [fileObj.id]: null }));
    try {
      const text = await getFileText(fileObj);
      if (!text || text.length < 20) {
        throw new Error('Could not extract enough text from this file.');
      }
      const truncated = truncateForAI(text, 3000);
      const skills = await extractSkillsFromDocument(truncated, []);

      setExtractedSkills(prev => ({ ...prev, [fileObj.id]: skills }));

      // Notify parent
      if (onSkillsExtracted) onSkillsExtracted(fileObj.id, skills);
    } catch (err) {
      console.error('[AI Extract Skills]', err);
      setAiErrors(prev => ({ ...prev, [fileObj.id]: err.message }));
    } finally {
      setAiActionLoading(prev => ({ ...prev, [fileObj.id]: null }));
    }
  };

  const handleAiGenerateBullets = async (fileObj) => {
    setAiActionLoading(prev => ({ ...prev, [fileObj.id]: 'bullets' }));
    setAiErrors(prev => ({ ...prev, [fileObj.id]: null }));
    try {
      const text = await getFileText(fileObj);
      if (!text || text.length < 20) {
        throw new Error('Could not extract enough text from this file.');
      }
      const truncated = truncateForAI(text, 3000);
      const bullets = await generateBulletsFromEvaluation(truncated);

      setGeneratedBullets(prev => ({ ...prev, [fileObj.id]: bullets }));

      // Notify parent
      if (onBulletsGenerated) onBulletsGenerated(fileObj.id, bullets);
    } catch (err) {
      console.error('[AI Generate Bullets]', err);
      setAiErrors(prev => ({ ...prev, [fileObj.id]: err.message }));
    } finally {
      setAiActionLoading(prev => ({ ...prev, [fileObj.id]: null }));
    }
  };

  const handleCopyBullet = (fileId, index, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedBullet(`${fileId}-${index}`);
      setTimeout(() => setCopiedBullet(null), 2000);
    });
  };

  // ─────────────── Existing handlers ───────────────

  const removeFile = (fileId) => {
    const updated = uploadedFiles.filter(f => f.id !== fileId);
    setUploadedFiles(updated);
    if (onFilesAdded) onFilesAdded(updated.map(({ file, ...rest }) => rest));
    // Clean up AI state
    setExtractedSkills(prev => { const n = { ...prev }; delete n[fileId]; return n; });
    setGeneratedBullets(prev => { const n = { ...prev }; delete n[fileId]; return n; });
    setTextCache(prev => { const n = { ...prev }; delete n[fileId]; return n; });
  };

  const updateSummary = (fileId, summary) => {
    const updated = uploadedFiles.map(f =>
      f.id === fileId ? { ...f, summary } : f
    );
    setUploadedFiles(updated);
    if (onFilesAdded) onFilesAdded(updated.map(({ file, ...rest }) => rest));
  };

  const updateFileCategory = (fileId, newCategory) => {
    const updated = uploadedFiles.map(f =>
      f.id === fileId ? { ...f, category: newCategory } : f
    );
    setUploadedFiles(updated);
    if (onFilesAdded) onFilesAdded(updated.map(({ file, ...rest }) => rest));
  };

  const handleDragStart = (e, fileId) => {
    setDraggedFile(fileId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, categoryId) => {
    e.preventDefault();
    setDragOverCategory(categoryId);
  };

  const handleDropFile = (e, targetCategory) => {
    e.preventDefault();
    if (draggedFile) {
      updateFileCategory(draggedFile, targetCategory);
      setDraggedFile(null);
      setDragOverCategory(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedFile(null);
    setDragOverCategory(null);
  };

  const filesByCategory = uploadedFiles.reduce((acc, file) => {
    if (!acc[file.category]) acc[file.category] = [];
    acc[file.category].push(file);
    return acc;
  }, {});

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div className="space-y-6">
      {/* Category Selection */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Select Portfolio Categories
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Select one or more categories. When you drag and drop files, they will be automatically organized into the selected categories.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => toggleCategory(category.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                selectedCategories.includes(category.id)
                  ? 'border-primary-600 bg-primary-50 text-primary-700 shadow-sm'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              }`}
            >
              {selectedCategories.includes(category.id) ? (
                <CheckSquare className="w-4 h-4" />
              ) : (
                <Square className="w-4 h-4" />
              )}
              {category.icon}
              <span className="text-sm font-medium">{category.label}</span>
            </button>
          ))}
        </div>
        {selectedCategories.length > 0 && (
          <div className="mt-4 p-3 bg-primary-50 rounded-lg">
            <p className="text-sm text-primary-700">
              <strong>{selectedCategories.length}</strong> categor{selectedCategories.length === 1 ? 'y' : 'ies'} selected: {selectedCategories.map(id => categories.find(c => c.id === id)?.label).join(', ')}
            </p>
          </div>
        )}
      </div>

      {/* Drag and Drop Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive
            ? 'border-primary-600 bg-primary-50'
            : 'border-gray-300 bg-gray-50 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-lg font-medium text-gray-700 mb-2">
          Drag and drop files here, or click to browse
        </p>
        <p className="text-sm text-gray-500 mb-4">
          {selectedCategories.length > 0
            ? `Files will be organized into: ${selectedCategories.map(id => categories.find(c => c.id === id)?.label).join(', ')}`
            : 'Files will be automatically categorized based on file type'}
        </p>
        <input
          type="file"
          multiple
          onChange={handleFileInput}
          className="hidden"
          id="file-upload"
        />
        <label
          htmlFor="file-upload"
          className="btn btn-primary inline-flex items-center space-x-2 cursor-pointer"
        >
          <Upload className="w-4 h-4" />
          <span>Select Files</span>
        </label>
      </div>

      {/* Organized Files by Category (hidden when hideExistingFiles is set — files are managed on Portfolio Files page) */}
      {!hideExistingFiles && Object.keys(filesByCategory).length > 0 && (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-gray-900">
            Portfolio Files Organized by Category ({uploadedFiles.length} total)
          </h3>
          
          {Object.entries(filesByCategory).map(([categoryId, files]) => {
            const category = categories.find(c => c.id === categoryId);
            return (
              <div
                key={categoryId}
                className={`card border-l-4 ${
                  dragOverCategory === categoryId ? 'border-primary-600 bg-primary-50' : ''
                }`}
                style={{ borderLeftColor: dragOverCategory === categoryId ? undefined : 'transparent' }}
                onDragOver={(e) => handleDragOver(e, categoryId)}
                onDrop={(e) => handleDropFile(e, categoryId)}
                onDragLeave={() => setDragOverCategory(null)}
              >
                <div className="flex items-center gap-3 mb-4">
                  {category?.icon}
                  <h4 className="text-lg font-semibold text-gray-900">
                    {category?.label || categoryId}
                  </h4>
                  <span className="text-sm text-gray-500">({files.length})</span>
                </div>
                
                <div className="space-y-3">
                  {files.map((fileObj) => {
                    const fileAiSupported = isAiSupported(fileObj);
                    const loadingAction = aiActionLoading[fileObj.id];
                    const fileError = aiErrors[fileObj.id];
                    const fileSkills = extractedSkills[fileObj.id];
                    const fileBullets = generatedBullets[fileObj.id];

                    return (
                      <div
                        key={fileObj.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, fileObj.id)}
                        onDragEnd={handleDragEnd}
                        className={`border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-all ${
                          draggedFile === fileObj.id ? 'opacity-50' : ''
                        }`}
                      >
                        {/* File header */}
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-3 flex-1">
                            <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
                            <div className="bg-primary-100 p-2 rounded-lg">
                              {category?.icon || <File className="w-5 h-5" />}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{fileObj.name}</p>
                              <p className="text-sm text-gray-500">
                                {formatFileSize(fileObj.size)} • Uploaded {new Date(fileObj.uploadedAt).toLocaleDateString()}
                              </p>
                              {/* S3 upload status */}
                              {uploadingFiles.has(fileObj.id) && (
                                <span className="inline-flex items-center gap-1 text-xs text-blue-600 mt-1">
                                  <Upload className="w-3.5 h-3.5 animate-pulse" /> Uploading to cloud...
                                </span>
                              )}
                              {uploadErrors[fileObj.id] && (
                                <span className="inline-flex items-center gap-1 text-xs text-red-600 mt-1">
                                  <AlertCircle className="w-3.5 h-3.5" /> Upload failed: {uploadErrors[fileObj.id]}
                                </span>
                              )}
                              {fileObj.url && !uploadingFiles.has(fileObj.id) && (
                                <a
                                  href={fileObj.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-700 mt-1"
                                >
                                  <Download className="w-3.5 h-3.5" /> Stored in cloud - Click to open
                                </a>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <select
                              value={fileObj.category}
                              onChange={(e) => updateFileCategory(fileObj.id, e.target.value)}
                              className="text-sm border border-gray-300 rounded px-2 py-1"
                            >
                              {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.label}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => removeFile(fileObj.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Summary section - show for all supported file types */}
                        {isAiSupported(fileObj) && (
                          <div className="mt-3">
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Summary
                              </label>
                              <div className="flex items-center gap-2">
                                {analyzingFiles.has(fileObj.id) && (
                                  <div className="flex items-center gap-1 text-sm text-gray-500">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    Analyzing...
                                  </div>
                                )}
                              </div>
                            </div>
                            <textarea
                              value={fileObj.summary || ''}
                              onChange={(e) => updateSummary(fileObj.id, e.target.value)}
                              className="textarea text-sm w-full"
                              rows="3"
                              placeholder="AI-generated summary will appear here. Edit to customize..."
                            />
                          </div>
                        )}

                        {/* AI Action Buttons */}
                        {fileAiSupported && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="flex flex-wrap items-center gap-2">
                              {/* Re-summarize */}
                              <button
                                type="button"
                                onClick={() => handleAiResummarize(fileObj)}
                                disabled={!!loadingAction}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                title="AI: Re-generate summary from document content"
                              >
                                {loadingAction === 'summarize' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                                {loadingAction === 'summarize' ? 'Summarizing...' : 'AI Summarize'}
                              </button>

                              {/* Extract Skills */}
                              <button
                                type="button"
                                onClick={() => handleAiExtractSkills(fileObj)}
                                disabled={!!loadingAction}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                title="AI: Extract skills from this document"
                              >
                                {loadingAction === 'skills' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lightbulb className="w-3.5 h-3.5" />}
                                {loadingAction === 'skills' ? 'Extracting...' : 'Extract Skills'}
                              </button>

                              {/* Generate Bullets */}
                              <button
                                type="button"
                                onClick={() => handleAiGenerateBullets(fileObj)}
                                disabled={!!loadingAction}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                title="AI: Generate resume bullet points from this document"
                              >
                                {loadingAction === 'bullets' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListChecks className="w-3.5 h-3.5" />}
                                {loadingAction === 'bullets' ? 'Generating...' : 'Generate Bullets'}
                              </button>
                            </div>

                            {/* Error message */}
                            {fileError && (
                              <div className="mt-2 p-2 bg-red-50 rounded-md">
                                <p className="text-xs text-red-600">{fileError}</p>
                              </div>
                            )}

                            {/* Extracted Skills Results */}
                            {fileSkills && fileSkills.length > 0 && (
                              <div className="mt-3 p-3 bg-purple-50 rounded-lg">
                                <h5 className="text-xs font-semibold text-purple-800 mb-2 flex items-center gap-1">
                                  <Lightbulb className="w-3.5 h-3.5" />
                                  Extracted Skills ({fileSkills.length})
                                </h5>
                                <div className="flex flex-wrap gap-1.5">
                                  {fileSkills.map((skill, idx) => (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-white text-purple-700 rounded-full border border-purple-200"
                                    >
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Generated Bullets Results */}
                            {fileBullets && fileBullets.length > 0 && (
                              <div className="mt-3 p-3 bg-purple-50 rounded-lg">
                                <h5 className="text-xs font-semibold text-purple-800 mb-2 flex items-center gap-1">
                                  <ListChecks className="w-3.5 h-3.5" />
                                  Resume Bullet Points ({fileBullets.length})
                                </h5>
                                <ul className="space-y-2">
                                  {fileBullets.map((bullet, idx) => (
                                    <li key={idx} className="flex items-start gap-2 group">
                                      <span className="text-purple-400 mt-0.5 text-xs">•</span>
                                      <span className="text-xs text-gray-800 flex-1">{bullet}</span>
                                      <button
                                        type="button"
                                        onClick={() => handleCopyBullet(fileObj.id, idx, bullet)}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-purple-100"
                                        title="Copy bullet"
                                      >
                                        {copiedBullet === `${fileObj.id}-${idx}` ? (
                                          <Check className="w-3.5 h-3.5 text-green-600" />
                                        ) : (
                                          <Copy className="w-3.5 h-3.5 text-purple-500" />
                                        )}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PortfolioUpload;
